import IconHighlight from "@shared/components/icons/Highlight"
import IconTextColor from "@shared/components/icons/TextColor"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import { HIGHLIGHT_COLORS } from "@shared/unified-tiptap/extensions/highlight/highlight-extension"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { CLEAR_SWATCH, pickerValue } from "./color-swatch-values"
import { ColorSplitControl, type SwatchItem } from "./ColorSplitControl"
import { useActiveMarkColor } from "./use-active-mark-color"

const HIGHLIGHT_COLOR_OPTIONS = [{ name: "Default", color: null }, ...HIGHLIGHT_COLORS]

/** The color the main button applies; the picker offers the full palette. */
const DEFAULT_HIGHLIGHT_COLOR = HIGHLIGHT_COLORS.find((entry) => entry.name === "Yellow")!.color

/** `null` clears the highlight; every other color sets it. */
function setHighlight(editor: Editor, color: string | null): void {
  const labeled = editor
    .chain()
    .label(OPERATION_CONTRACTS.OP_SET_HIGHLIGHT, "floating-toolbar", { color })
  if (color === null) labeled.unsetHighlight().run()
  else labeled.toggleHighlight({ color }).run()
}

export function HighlightPopover({ editor }: { editor: Editor }) {
  const activeHighlightColor = useActiveMarkColor(editor, "highlight")
  const isActive = !!activeHighlightColor
  const canToggle = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.can().toggleHighlight(),
  })

  const items: SwatchItem[] = HIGHLIGHT_COLOR_OPTIONS.map((entry) => ({
    color: entry.color ?? CLEAR_SWATCH,
    ariaLabel: `${entry.name} highlight`,
    glyph: (
      <div className="-m-0.5 flex p-0.5" style={{ backgroundColor: entry.color ?? "white" }}>
        <IconTextColor />
      </div>
    ),
    onSelect: () => setHighlight(editor, entry.color),
  }))

  return (
    <ColorSplitControl
      isDisabled={!canToggle}
      isActive={isActive}
      applyLabel="Highlight"
      // The main button toggles the default highlight color; the picker offers the rest.
      onApply={() => setHighlight(editor, isActive ? null : DEFAULT_HIGHLIGHT_COLOR)}
      pickerLabel="Highlight color"
      icon={<IconHighlight boxColor={activeHighlightColor ?? undefined} />}
      items={items}
      selectedColor={pickerValue(activeHighlightColor)}
    />
  )
}
